import { Box, Stack, Typography, useTheme, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { RoadmapResponse, RoadmapPhase, RoadmapWeek } from "../../api/client";
import StatusDot from "./StatusDot";
import ProgressBar from "./ProgressBar";

interface RoadmapMinimapProps {
  roadmap: RoadmapResponse;
  progress: { done: number; total: number; pct: number };
  currentWeekIndex: number | null;
}

const HIDE_BELOW = 1100; // raw px per spec §6.1 (no MUI preset matches exactly)

// Spec §6.1 — sticky 220px column, full-height with own scroll. Hides
// completely below 1100px (mid-width users get scroll-only navigation).
// Anchor links jump to #week-{index} which we set as DOM ids on each
// WeekRail container.
export default function RoadmapMinimap({
  roadmap,
  progress,
  currentWeekIndex,
}: RoadmapMinimapProps) {
  const { t } = useTranslation("roadmap");

  return (
    <Box
      component="aside"
      aria-label={t("RoadmapMinimap.navAriaLabel")}
      sx={{
        width: 220,
        flexShrink: 0,
        position: "sticky",
        top: 0,
        height: "100vh",
        overflowY: "auto",
        borderRight: "1px solid",
        borderColor: "divider",
        p: 2.5,
        [`@media (max-width: ${HIDE_BELOW - 1}px)`]: {
          display: "none",
        },
      }}
    >
      {/* Progress composite */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography
            component="span"
            sx={{
              fontFamily: "monospace",
              fontSize: 20,
              fontWeight: 500,
              color: "secondary.main",
              letterSpacing: "-0.02em",
            }}
          >
            {progress.done}
            <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>
              /{progress.total}
            </Box>
          </Typography>
          <Typography
            component="span"
            sx={{
              fontFamily: "monospace",
              fontSize: 13,
              fontWeight: 500,
              color: "text.secondary",
            }}
          >
            {progress.pct}%
          </Typography>
        </Stack>
        <ProgressBar
          value={progress.done}
          max={progress.total}
          ariaLabel={t("RoadmapMinimap.progressAriaLabel", { done: progress.done, total: progress.total })}
        />
      </Box>

      {/* Phase groups */}
      <Stack spacing={2.5}>
        {roadmap.phases.map((phase) => (
          <PhaseGroup
            key={phase.index}
            phase={phase}
            currentWeekIndex={currentWeekIndex}
          />
        ))}
      </Stack>
    </Box>
  );
}

function PhaseGroup({
  phase,
  currentWeekIndex,
}: {
  phase: RoadmapPhase;
  currentWeekIndex: number | null;
}) {
  const tasks = phase.weeks.flatMap((w) => w.tasks ?? []);
  const done = tasks.filter((t) => t.done).length;
  const total = tasks.length;

  return (
    <Box>
      {/* Phase row: dot + name + n/m */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1, pr: 0.5 }}
      >
        <StatusDot status={phase.status} size="sm" />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "text.primary",
            flex: 1,
          }}
        >
          {phase.title}
        </Typography>
        {total > 0 && (
          <Typography
            variant="caption"
            sx={{
              fontFamily: "monospace",
              color: "text.secondary",
              fontWeight: 500,
            }}
          >
            {done}/{total}
          </Typography>
        )}
      </Stack>

      {/* Week rows — anchors to #week-N */}
      <Stack spacing={0.25} sx={{ pl: 2 }}>
        {phase.weeks.map((week) => (
          <WeekLink
            key={week.index}
            week={week}
            isCurrent={currentWeekIndex === week.index}
          />
        ))}
      </Stack>
    </Box>
  );
}

function WeekLink({ week, isCurrent }: { week: RoadmapWeek; isCurrent: boolean }) {
  const theme = useTheme();
  const tasks = week.tasks ?? [];
  const done = tasks.filter((t) => t.done).length;
  const total = tasks.length;

  return (
    <Box
      component="a"
      href={`#week-${week.index}`}
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 1,
        textDecoration: "none",
        color: isCurrent ? "secondary.main" : "text.secondary",
        fontWeight: 500,
        fontSize: 12.5,
        lineHeight: 1.4,
        py: 0.5,
        px: 1,
        ml: -1,
        borderRadius: 1,
        bgcolor: isCurrent ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
        transition: theme.transitions.create(["background-color", "color"], {
          duration: theme.transitions.duration.shortest,
        }),
        "&:hover": {
          bgcolor: isCurrent
            ? alpha(theme.palette.secondary.main, 0.15)
            : alpha(theme.palette.text.primary, 0.04),
          color: isCurrent ? "secondary.main" : "text.primary",
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.secondary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <Box
          component="span"
          sx={{ fontFamily: "monospace", fontSize: 11, opacity: 0.7, mr: 0.5 }}
        >
          W{week.index}
        </Box>
        {week.title}
      </Box>
      {total > 0 && (
        <Box
          component="span"
          sx={{
            fontFamily: "monospace",
            fontSize: 10.5,
            opacity: 0.7,
            flexShrink: 0,
          }}
        >
          {done}/{total}
        </Box>
      )}
    </Box>
  );
}
